import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import JSZip from 'jszip'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import api, { attendanceAPI, employeeGroupAPI, salaryAdvanceAPI } from '../../services/api'
import PayrollHeader from './PayrollHeader'
import { reportLayoutConfig as config } from './config/reportLayoutConfig'
import { calculateRows, MONTH_NUMBERS } from './utils/salaryCalculations'
import { groupRowsByEmployeeGroup, PF_ENABLED_GROUP_NAME } from './config/groupingConfig'
import { paginateSections, FOOTER_LABELS } from './utils/paginate'
import { exportSalaryStatementToExcel, buildSalaryStatementExcelBlob } from './utils/salaryStatementExcel'
import { buildSalaryAdvanceExcelBlob } from '../salaryadvance/utils/salaryAdvanceExcel'
import { toTitleCase } from './utils/textFormat'
import { useAuth } from '../../context/AuthContext'
import './SalaryStatement.css'

function formatNumber(value) {
  if (value === null || value === undefined) return 'N/A'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })
}

// Blank (not 0 or N/A) for the Advance Verification table - a genuinely
// empty cell is the signal worth noticing there, not a formatted zero.
function formatOrBlank(value) {
  const num = Number(value)
  if (!value || !num) return ''
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

// OT amount is rounded UP (not to nearest), matching the Attendance Report's
// OT Total display - the underlying value stays exact for the actual net
// salary math, this only affects what's shown here.
function formatOtAmount(value) {
  if (value === null || value === undefined) return 'N/A'
  return Math.ceil(Number(value)).toLocaleString()
}

function computeTotals(rows) {
  return rows.reduce(
    (acc, row) => ({
      grossSalary: acc.grossSalary + Number(row.gross_salary),
      paySalary: acc.paySalary + (row.paySalary ?? 0),
      otherOt: acc.otherOt + Number(row.other_ot ?? 0),
      otAmount: acc.otAmount + Number(row.overtime_amount),
      paySalaryWithOt: acc.paySalaryWithOt + (row.paySalaryWithOt ?? 0),
      advance: acc.advance + Number(row.advance_deduction),
      netSalary: acc.netSalary + (row.netSalary ?? 0),
      basicSalary: acc.basicSalary + (row.basicSalary ?? 0),
      pf: acc.pf + (row.pf ?? 0),
      esi: acc.esi + (row.esi ?? 0),
      totalDeduction: acc.totalDeduction + (row.totalDeduction ?? 0),
      totalPayableSalary: acc.totalPayableSalary + (row.totalPayableSalary ?? 0),
    }),
    {
      grossSalary: 0, paySalary: 0, otherOt: 0, otAmount: 0, paySalaryWithOt: 0, advance: 0, netSalary: 0,
      basicSalary: 0, pf: 0, esi: 0, totalDeduction: 0, totalPayableSalary: 0,
    }
  )
}

function SalaryStatement() {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  // Very few people are expected to hold this - see app/api/payroll.py's
  // /unlock-payment and its docstring on why unlocking is permission-gated
  // instead of time-limited.
  const canUnlockPayment = hasPermission('payroll.unlock_payment')
  const rawRows = location.state?.rows || []
  const currentYear = new Date().getFullYear()
  const calculatedRows = rawRows.length > 0 ? calculateRows(rawRows, currentYear) : []
  // Every row gets a stable global index (used as the key for Paid/Confirmed
  // state) before being split into group sections, so state stays correct no
  // matter which section a row ends up rendered under.
  const rows = calculatedRows.map((row, index) => ({ ...row, globalIndex: index }))
  // Section order follows the "Pay Groups" admin tab's configured order
  // (falls back to alphabetical for any group name not yet loaded/known).
  const [groupOrder, setGroupOrder] = useState([])
  useEffect(() => {
    employeeGroupAPI.getAll()
      .then((response) => setGroupOrder(response.data.map((g) => g.group_name)))
      .catch(() => {})
  }, [])
  const sections = groupRowsByEmployeeGroup(rows, groupOrder)
  // Each entry is exactly one physical printed page (manual pagination - see
  // utils/paginate.js for why this replaced letting the browser flow a
  // group's table across pages natively).
  const pages = paginateSections(sections, config)

  const [paidMap, setPaidMap] = useState(() => {
    const initial = {}
    rows.forEach((row) => {
      if (row.already_confirmed) initial[row.globalIndex] = true
    })
    return initial
  })
  const [confirmedIndexes, setConfirmedIndexes] = useState(() =>
    rows.filter((row) => row.already_confirmed).map((row) => row.globalIndex)
  )
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [confirmErrorList, setConfirmErrorList] = useState([])
  const [bankLetterSelected, setBankLetterSelected] = useState({})
  const [isDownloadingAll, setIsDownloadingAll] = useState(false)
  const [downloadAllError, setDownloadAllError] = useState('')
  // One entry per rendered page-block (see pages.map below) - used to
  // render each physical page to its own PDF page in handleDownloadAll.
  const pageRefs = useRef([])

  if (rawRows.length === 0) {
    return (
      <>
        <PayrollHeader />
        <div className="statement-page">
          <p>No data to display. Please upload a Salary Excel file first.</p>
          <button onClick={() => navigate('/payroll')}>Go to Upload</button>
        </div>
      </>
    )
  }

  const month = rows[0].month
  const preConfirmedCount = confirmedIndexes.length

  function togglePaid(globalIndex) {
    setPaidMap((prev) => ({ ...prev, [globalIndex]: !prev[globalIndex] }))
  }

  // Confirming locks a row immediately - no self-service way back, by
  // design. The only way past it is handleUnlockPayment below, gated by
  // the payroll.unlock_payment permission server-side.
  async function handleUnlockPayment(row) {
    if (!window.confirm(`Unlock ${row.employee}'s ${row.month} ${currentYear} payment? This makes them unconfirmed again.`)) return
    const reason = window.prompt('Optional: why is this being unlocked? (helps the audit trail)', '') || ''
    setConfirmError('')
    try {
      await api.post('/payroll/unlock-payment', null, {
        params: { employee_id: row.employee_id, month: row.month, year: currentYear, reason },
      })
      setConfirmedIndexes((prev) => prev.filter((idx) => idx !== row.globalIndex))
      setPaidMap((prev) => ({ ...prev, [row.globalIndex]: false }))
    } catch (error) {
      setConfirmError(error.response?.data?.detail || 'Could not unlock this payment.')
    }
  }

  function toggleBankLetterSelected(globalIndex) {
    setBankLetterSelected((prev) => ({ ...prev, [globalIndex]: !prev[globalIndex] }))
  }

  // Shared between both table layouts (regular groups and the PF
  // group) - identical Paid/Bank Letter behavior regardless of which columns
  // surround them.
  function renderPaidCell(row) {
    const isConfirmed = confirmedIndexes.includes(row.globalIndex)
    return (
      <td className="no-print">
        <select
          value={paidMap[row.globalIndex] ? 'yes' : 'no'}
          onChange={() => togglePaid(row.globalIndex)}
          disabled={isConfirmed}
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
        {isConfirmed && (
          <>
            <span className="confirmed-badge">Locked</span>
            {canUnlockPayment && (
              <button type="button" className="unlock-button no-print" onClick={() => handleUnlockPayment(row)}>
                Unlock
              </button>
            )}
          </>
        )}
      </td>
    )
  }

  function renderBankLetterCell(row) {
    const missingBankInfo = !row.account_number
    const locked = confirmedIndexes.includes(row.globalIndex)
    const disabled = missingBankInfo || locked
    const title = missingBankInfo
      ? 'No bank account on file for this employee'
      : locked
      ? 'Paid is Locked for this employee - already paid, no longer needs a Bank Letter'
      : undefined
    return (
      <td className="no-print">
        <input
          type="checkbox"
          checked={!!bankLetterSelected[row.globalIndex]}
          onChange={() => toggleBankLetterSelected(row.globalIndex)}
          disabled={disabled}
          title={title}
        />
      </td>
    )
  }

  // Shared Account Name/Account Number cells - highlighted (not just blank)
  // when the employee has no bank account on file, so the gap is visible
  // instead of silently vanishing. Separately flagged (a different color) if
  // the "pay to account" bank account on file is marked Secondary rather
  // than Primary - not an error, just worth a second look.
  function renderAccountCells(row) {
    const missingBankInfo = !row.account_number
    const isSecondaryAccount = !missingBankInfo && row.account_type === 'Secondary'
    const cellClass = missingBankInfo
      ? ' missing-bank-info'
      : isSecondaryAccount
      ? ' secondary-account-info'
      : ''
    const cellTitle = isSecondaryAccount ? 'Pay-to account is marked Secondary, not Primary' : undefined
    return (
      <>
        <td className={`no-print align-left${cellClass}`} title={cellTitle}>{toTitleCase(row.account_name) || ''}</td>
        <td className={`no-print${cellClass}`} title={cellTitle}>{row.account_number || ''}</td>
        <td className={`no-print${cellClass}`} title={cellTitle}>{row.bank_name || ''}</td>
      </>
    )
  }

  // A row can be picked for the Bank Letter only if it has a bank account
  // on file and Paid isn't Locked for it - matches renderBankLetterCell's
  // own disabled condition, reused by Select All so it can't silently
  // select a row whose individual checkbox is disabled.
  function isBankLetterSelectable(row) {
    const missingBankInfo = !row.account_number
    const locked = confirmedIndexes.includes(row.globalIndex)
    return !missingBankInfo && !locked
  }

  function toggleSectionBankLetterSelected(sectionRows) {
    const selectable = sectionRows.filter(isBankLetterSelectable)
    setBankLetterSelected((prev) => {
      const allSelected = selectable.length > 0 && selectable.every((row) => prev[row.globalIndex])
      const next = { ...prev }
      selectable.forEach((row) => {
        next[row.globalIndex] = !allSelected
      })
      return next
    })
  }

  // Already-confirmed rows are locked (their Paid select is disabled) and
  // excluded here - Select All only ever touches rows still open to change.
  function toggleSectionPaid(sectionRows) {
    const togglable = sectionRows.filter((row) => !confirmedIndexes.includes(row.globalIndex))
    setPaidMap((prev) => {
      const allPaid = togglable.length > 0 && togglable.every((row) => prev[row.globalIndex])
      const next = { ...prev }
      togglable.forEach((row) => {
        next[row.globalIndex] = !allPaid
      })
      return next
    })
  }

  function handleGenerateBankLetter() {
    const selectedRows = rows.filter((row) => bankLetterSelected[row.globalIndex])
    navigate('/payroll/bank-letter', { state: { rows: selectedRows } })
  }

  // Renders every already-paginated page-block (pageRefs, one per physical
  // page - see pages.map below) to its own PDF page via html2canvas, so
  // pagination doesn't need to be solved again here. Both libraries are
  // dynamically imported so they're only ever downloaded by someone who
  // actually clicks "Download All", not added to every page load.
  async function buildSalaryStatementPdfBlob() {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ])

    const pageNodes = pageRefs.current.filter(Boolean)
    let doc = null

    for (const node of pageNodes) {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        // Interactive/operational columns (Paid, Account Name/Number, Bank
        // Letter, Confirm button) are only hidden via @media print - on
        // screen they're visible, so they'd otherwise end up baked into the
        // image. Skip anything print already hides for a clean PDF.
        ignoreElements: (el) => el.classList?.contains('no-print'),
      })

      const isLandscape = canvas.width >= canvas.height
      const pageWidthMm = isLandscape ? 297 : 210
      const pageHeightMm = isLandscape ? 210 : 297
      const marginMm = 5
      const maxWidthMm = pageWidthMm - marginMm * 2
      const maxHeightMm = pageHeightMm - marginMm * 2
      const canvasRatio = canvas.width / canvas.height

      let imgWidthMm = maxWidthMm
      let imgHeightMm = imgWidthMm / canvasRatio
      if (imgHeightMm > maxHeightMm) {
        imgHeightMm = maxHeightMm
        imgWidthMm = imgHeightMm * canvasRatio
      }
      const xMm = (pageWidthMm - imgWidthMm) / 2

      if (!doc) {
        doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' })
      } else {
        doc.addPage('a4', isLandscape ? 'landscape' : 'portrait')
      }
      doc.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', xMm, marginMm, imgWidthMm, imgHeightMm)
    }

    return doc.output('blob')
  }

  // Bundles the Attendance export (same data the View Attendance screen
  // exports), the Salary Statement Excel export, and a PDF snapshot of this
  // page into one Zip, for saving everything together in one download.
  async function handleDownloadAll() {
    setDownloadAllError('')
    setIsDownloadingAll(true)
    // Every file is independently best-effort - one failing (missing data,
    // a permission gap, a transient request error) skips just that file
    // and the rest of the zip still gets built, rather than the whole
    // download aborting over a single report.
    const skipped = []
    try {
      const monthNumber = MONTH_NUMBERS[String(month).trim().toLowerCase()]
      // Inner files: <Name>_MMYY (e.g. Aug 2026 -> 0826, Sep 2026 -> 0926).
      // Zip itself: Reports_<Mon>_<Year> (e.g. Reports_Aug_2026) - a
      // different, more human-readable format than the compact one used
      // inside, per explicit request.
      const mmyy = `${String(monthNumber).padStart(2, '0')}${String(currentYear).slice(-2)}`
      const monthAbbr = String(month).slice(0, 3)
      const zip = new JSZip()

      try {
        const attendanceResponse = await attendanceAPI.exportRegular(monthNumber, currentYear)
        zip.file(`ATS_${mmyy}.xlsx`, attendanceResponse.data)
      } catch (e) {
        skipped.push('Attendance')
      }

      try {
        const salaryExcelBlob = buildSalaryStatementExcelBlob(sections, month, currentYear, paidMap, confirmedIndexes)
        zip.file(`DetailReport_${mmyy}.xlsx`, salaryExcelBlob)
      } catch (e) {
        skipped.push('Salary Statement (Excel)')
      }

      try {
        const salaryPdfBlob = await buildSalaryStatementPdfBlob()
        zip.file(`Webpage_${mmyy}.pdf`, salaryPdfBlob)
      } catch (e) {
        skipped.push('Salary Statement (PDF)')
      }

      try {
        const otResponse = await attendanceAPI.exportOT(monthNumber, currentYear)
        zip.file(`OT_${mmyy}.xlsx`, otResponse.data)
      } catch (e) {
        skipped.push('OT Report')
      }

      try {
        const advanceResponse = await salaryAdvanceAPI.getReport(monthNumber, currentYear)
        const salaryAdvanceExcelBlob = buildSalaryAdvanceExcelBlob(advanceResponse.data, monthNumber, currentYear)
        zip.file(`AdvanceData_${mmyy}.xlsx`, salaryAdvanceExcelBlob)
      } catch (e) {
        skipped.push('Salary Advance Report')
      }

      if (Object.keys(zip.files).length === 0) {
        setDownloadAllError('Could not generate any of the reports. Please try again.')
        return
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' })

      const url = window.URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Reports_${monthAbbr}_${currentYear}.zip`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      if (skipped.length > 0) {
        setDownloadAllError(`Downloaded, but skipped (could not generate): ${skipped.join(', ')}.`)
      }
    } catch (error) {
      setDownloadAllError('Could not download. Please try again.')
    } finally {
      setIsDownloadingAll(false)
    }
  }

  async function handleConfirmSave(sectionRows) {
    setConfirmMessage('')
    setConfirmError('')
    setConfirmErrorList([])

    const rowsToSave = sectionRows.filter(
      (row) => paidMap[row.globalIndex] && !confirmedIndexes.includes(row.globalIndex)
    )

    if (rowsToSave.length === 0) {
      setConfirmError('Mark at least one record as "Paid = Yes" before confirming.')
      return
    }

    setIsConfirming(true)
    try {
      const payload = {
        rows: rowsToSave.map((row) => ({
          employee_id: row.employee_id,
          employee: row.employee,
          department: row.department,
          month: row.month,
          year: currentYear,
          gross_salary: Number(row.gross_salary),
          total_attendance: Number(row.total_attendance),
          leave: Number(row.leave),
          working_days: Number(row.workingDays),
          days_in_month: Number(row.daysInMonth),
          pay_salary: Number(row.paySalary),
          overtime_amount: Number(row.overtime_amount),
          pay_salary_with_ot: Number(row.paySalaryWithOt),
          advance_deduction: Number(row.advance_deduction),
          net_salary: Number(row.netSalary),
        })),
      }

      const response = await api.post('/payroll/confirm-payment', payload)

      setConfirmMessage(response.data.message)
      setConfirmedIndexes((prev) => [...prev, ...rowsToSave.map((row) => row.globalIndex)])
    } catch (error) {
      const detail = error.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.already_confirmed) {
        setConfirmError(detail.message)
        setConfirmErrorList(detail.already_confirmed)
      } else if (detail) {
        setConfirmError(typeof detail === 'string' ? detail : 'Could not save. Please try again.')
      } else {
        setConfirmError('Could not reach the server. Is the backend running?')
      }
    } finally {
      setIsConfirming(false)
    }
  }

  // Advance Verification table (screen-only, see the no-print wrapper below)
  // - only employees with a nonzero Advance Deducted and/or Advance Entry,
  // so a blank Advance Entry next to a real Advance Deducted (or vice
  // versa) is easy to spot without wading through everyone who has
  // neither. Sorted EMI rows
  // first (an "EMI + Advance" row counts as EMI for this), then plain
  // Advance, then rows with no Salary Advance type at all (a purely manual
  // Attendance-page Adv Amt) - a plain .sort() is stable in every browser
  // this app supports, so each group keeps its original relative order.
  const advanceTypeRank = (advanceType) => {
    if ((advanceType || '').includes('EMI')) return 0
    if (advanceType) return 1
    return 2
  }
  const advanceVerificationRows = rows
    .filter((row) => Number(row.advance_deduction || 0) || Number(row.advance_given || 0))
    .slice()
    .sort((a, b) => advanceTypeRank(a.advance_type) - advanceTypeRank(b.advance_type))
  const advanceVerificationTotals = advanceVerificationRows.reduce(
    (acc, row) => ({
      advance: acc.advance + Number(row.advance_deduction || 0),
      advanceGiven: acc.advanceGiven + Number(row.advance_given || 0),
    }),
    { advance: 0, advanceGiven: 0 }
  )

  return (
    <>
      <PayrollHeader className="no-print" />
      <div className="statement-page">
      {/* @page rule pulled from the config file (Section 17) instead of being hardcoded here.
          No "size" here on purpose - even "size: A4" with no orientation keyword
          makes Chrome grey out the print dialog's Layout control and force
          portrait. Leaving size unset is what keeps Portrait/Landscape genuinely
          user-controlled there; SalaryStatement.css reacts to whichever the user
          picks via @media print and (orientation: ...). */}
      <style>{`
        @page {
          margin: ${config.pageMarginCm}cm;
        }
      `}</style>

      <div className="statement-toolbar no-print">
        <div className="toolbar-left">
          <button className="back-button" onClick={() => navigate('/payroll')}>
            <span aria-hidden="true">&#8592;</span> Back to Upload
          </button>
          <button onClick={() => window.print()}>Print Salary Statement</button>
        </div>
        <div className="toolbar-right">
          <button
            onClick={() => exportSalaryStatementToExcel(sections, month, currentYear, paidMap, confirmedIndexes)}
          >
            Export to Excel
          </button>
          <button
            onClick={handleGenerateBankLetter}
            disabled={Object.values(bankLetterSelected).every((selected) => !selected)}
          >
            Generate Bank Letter
          </button>
          <button
            onClick={handleDownloadAll}
            disabled={isDownloadingAll}
            title="Export All (Attendance + Salary Statement + Salary Advance, as a Zip)"
          >
            Export All <FolderZipIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
          </button>
        </div>
      </div>
      {downloadAllError && <p className="error-message no-print">{downloadAllError}</p>}

      {preConfirmedCount > 0 && (
        <p className="info-message no-print">
          {preConfirmedCount} record(s) across this report were already confirmed/paid for {month} {currentYear} -
          shown as "Locked" below.
        </p>
      )}
      {confirmMessage && <p className="success-message no-print">{confirmMessage}</p>}
      {confirmError && <p className="error-message no-print">{confirmError}</p>}
      {confirmErrorList.length > 0 && (
        <ul className="row-errors no-print">
          {confirmErrorList.map((name, index) => (
            <li key={index}>{name}</li>
          ))}
        </ul>
      )}

      {pages.map((blocks, pageIndex) => (
        <div
          key={pageIndex}
          ref={(el) => { pageRefs.current[pageIndex] = el }}
          className={`physical-page${pageIndex > 0 ? ' group-section-break' : ''}`}
        >
          {blocks.map((page, blockIndex) => {
            const totals = computeTotals(page.groupRows)
            const isPfGroup = page.groupName === PF_ENABLED_GROUP_NAME
            return (
          <div
            key={blockIndex}
            className="statement-sheet group-section"
            style={{
              '--report-font-size': config.fontSize,
              '--report-heading-font-size': config.headingFontSize,
              '--report-subheading-font-size': config.subheadingFontSize,
              '--report-row-height': config.rowHeight,
              '--report-border-thickness': config.borderThickness,
            }}
          >
            <h1 className="statement-heading">
              ISKCON BANGALORE HUBLI CENTRE
              <span className="page-number-label">Page {pageIndex + 1}</span>
            </h1>
            <h2 className="statement-subheading">
              SALARY STATEMENT FOR THE MONTH OF {String(month).toUpperCase()} {currentYear} - {page.groupName.toUpperCase()}
            </h2>

            <div className="group-toolbar no-print">
              <button
                className="confirm-button"
                onClick={() => handleConfirmSave(page.groupRows)}
                disabled={isConfirming}
              >
                {isConfirming ? 'Saving...' : `Confirm & Save Payment - ${page.groupName}`}
              </button>
            </div>

            <table className="statement-table">
              {isPfGroup ? (
                <>
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th className="align-left">Employee</th>
                      <th>Gross Salary</th>
                      <th>Dept</th>
                      <th className="col-narrow">Days in Month</th>
                      <th className="col-narrow">Working Days</th>
                      <th className="col-narrow">Leave</th>
                      <th className="col-narrow">Total Attendance</th>
                      <th>Calc Salary</th>
                      <th>Advance Deducted</th>
                      <th>Basic (50%)</th>
                      <th className="col-narrow">PF (12%)</th>
                      <th className="col-narrow">ESI (0.75%)</th>
                      <th>Total Deduction</th>
                      <th>Total Payable Salary</th>
                      <th className="no-print">
                        <label className="check-all-header-label">
                          <input
                            type="checkbox"
                            checked={
                              page.groupRows.filter((row) => !confirmedIndexes.includes(row.globalIndex)).length > 0 &&
                              page.groupRows
                                .filter((row) => !confirmedIndexes.includes(row.globalIndex))
                                .every((row) => paidMap[row.globalIndex])
                            }
                            onChange={() => toggleSectionPaid(page.groupRows)}
                          />
                          Paid
                        </label>
                      </th>
                      <th className="no-print align-left">Account Name</th>
                      <th className="no-print">Account Number</th>
                      <th className="no-print">Bank</th>
                      <th className="no-print">
                        <label className="check-all-header-label">
                          <input
                            type="checkbox"
                            checked={
                              page.groupRows.filter(isBankLetterSelectable).length > 0 &&
                              page.groupRows.filter(isBankLetterSelectable).every((row) => bankLetterSelected[row.globalIndex])
                            }
                            onChange={() => toggleSectionBankLetterSelected(page.groupRows)}
                          />
                          Bank Letter
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.rows.map((row) => (
                      <tr key={row.globalIndex}>
                        <td>{row.slNo}</td>
                        <td className="align-left">{row.employee}</td>
                        <td>{formatNumber(row.gross_salary)}</td>
                        <td>{row.department}</td>
                        <td>{row.daysInMonth ?? 'N/A'}</td>
                        <td>{row.workingDays}</td>
                        <td>{row.leave}</td>
                        <td>{row.total_attendance}</td>
                        <td>{formatNumber(row.paySalary)}</td>
                        <td>{formatNumber(row.advance_deduction)}</td>
                        <td>{formatNumber(row.basicSalary)}</td>
                        <td>{formatNumber(row.pf)}</td>
                        <td>{formatNumber(row.esi)}</td>
                        <td>{formatNumber(row.totalDeduction)}</td>
                        <td>{formatNumber(row.totalPayableSalary)}</td>
                        {renderPaidCell(row)}
                        {renderAccountCells(row)}
                        {renderBankLetterCell(row)}
                      </tr>
                    ))}
                    {page.isLastPageOfGroup && (
                      <tr className="total-row">
                        <td colSpan={2} className="total-label">TOTAL</td>
                        <td>{formatNumber(totals.grossSalary)}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{formatNumber(totals.paySalary)}</td>
                        <td>{formatNumber(totals.advance)}</td>
                        <td>{formatNumber(totals.basicSalary)}</td>
                        <td>{formatNumber(totals.pf)}</td>
                        <td>{formatNumber(totals.esi)}</td>
                        <td>{formatNumber(totals.totalDeduction)}</td>
                        <td>{formatNumber(totals.totalPayableSalary)}</td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                      </tr>
                    )}
                    {page.isLastPageOfGroup && (
                      <tr className="signature-row">
                        <td colSpan={100} className="signature-cell">
                          <div className="signature-inner">
                            {FOOTER_LABELS.map((label) => (
                              <span key={label}>{label}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th className="align-left">Employee</th>
                      <th>Gross Salary</th>
                      <th>Dept</th>
                      <th className="col-narrow">Days in Month</th>
                      <th className="col-narrow">Working Days</th>
                      <th className="col-narrow">Leave</th>
                      <th className="col-narrow">Total Attendance</th>
                      <th>Calc Salary</th>
                      <th className="col-narrow">OT Days</th>
                      <th className="col-narrow">OT Hrs</th>
                      <th>OT Other</th>
                      <th>Total OT Amt</th>
                      <th>Salary + OT</th>
                      <th>Advance Deducted</th>
                      <th>Pay Salary</th>
                      <th className="no-print">
                        <label className="check-all-header-label">
                          <input
                            type="checkbox"
                            checked={
                              page.groupRows.filter((row) => !confirmedIndexes.includes(row.globalIndex)).length > 0 &&
                              page.groupRows
                                .filter((row) => !confirmedIndexes.includes(row.globalIndex))
                                .every((row) => paidMap[row.globalIndex])
                            }
                            onChange={() => toggleSectionPaid(page.groupRows)}
                          />
                          Paid
                        </label>
                      </th>
                      <th className="no-print align-left">Account Name</th>
                      <th className="no-print">Account Number</th>
                      <th className="no-print">Bank</th>
                      <th className="no-print">
                        <label className="check-all-header-label">
                          <input
                            type="checkbox"
                            checked={
                              page.groupRows.filter(isBankLetterSelectable).length > 0 &&
                              page.groupRows.filter(isBankLetterSelectable).every((row) => bankLetterSelected[row.globalIndex])
                            }
                            onChange={() => toggleSectionBankLetterSelected(page.groupRows)}
                          />
                          Bank Letter
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.rows.map((row) => (
                      <tr key={row.globalIndex}>
                        <td>{row.slNo}</td>
                        <td className="align-left">{row.employee}</td>
                        <td>{formatNumber(row.gross_salary)}</td>
                        <td>{row.department}</td>
                        <td>{row.daysInMonth ?? 'N/A'}</td>
                        <td>{row.workingDays}</td>
                        <td>{row.leave}</td>
                        <td>{row.total_attendance}</td>
                        <td>{formatNumber(row.paySalary)}</td>
                        <td>{row.ot_days ?? 0}</td>
                        <td>{row.ot_hours ?? 0}</td>
                        <td>{formatNumber(row.other_ot)}</td>
                        <td>{formatOtAmount(row.overtime_amount)}</td>
                        <td>{formatNumber(row.paySalaryWithOt)}</td>
                        <td>{formatNumber(row.advance_deduction)}</td>
                        <td>{formatNumber(row.netSalary)}</td>
                        {renderPaidCell(row)}
                        {renderAccountCells(row)}
                        {renderBankLetterCell(row)}
                      </tr>
                    ))}
                    {page.isLastPageOfGroup && (
                      <tr className="total-row">
                        <td colSpan={2} className="total-label">TOTAL</td>
                        <td>{formatNumber(totals.grossSalary)}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{formatNumber(totals.paySalary)}</td>
                        <td></td>
                        <td></td>
                        <td>{formatNumber(totals.otherOt)}</td>
                        <td>{formatOtAmount(totals.otAmount)}</td>
                        <td>{formatNumber(totals.paySalaryWithOt)}</td>
                        <td>{formatNumber(totals.advance)}</td>
                        <td>{formatNumber(totals.netSalary)}</td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                        <td className="no-print"></td>
                      </tr>
                    )}
                    {page.isLastPageOfGroup && (
                      <tr className="signature-row">
                        <td colSpan={100} className="signature-cell">
                          <div className="signature-inner">
                            {FOOTER_LABELS.map((label) => (
                              <span key={label}>{label}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
            )
          })}
        </div>
      ))}

      <div
        className="no-print"
        style={{
          maxWidth: '1400px',
          margin: '32px auto',
          background: '#ffffff',
          border: '2px solid #0f172a',
          padding: '16px',
          boxSizing: 'border-box',
        }}
      >
        <h1 className="statement-heading">ISKCON BANGALORE HUBLI CENTRE</h1>
        <h2 className="statement-subheading">Advance Verification</h2>
        <table className="statement-table">
          <thead>
            <tr>
              <th className="align-left">Employee</th>
              <th className="align-left">Department</th>
              <th>Advance Type</th>
              <th>Advance Deducted</th>
              <th>Advance Entry</th>
            </tr>
          </thead>
          <tbody>
            {advanceVerificationRows.map((row) => (
              <tr key={row.globalIndex}>
                <td className="align-left">{row.employee}</td>
                <td className="align-left">{row.department}</td>
                <td>{row.advance_type || '—'}</td>
                <td>{formatOrBlank(row.advance_deduction)}</td>
                <td>{formatOrBlank(row.advance_given)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td colSpan={3} className="total-label">TOTAL</td>
              <td>{formatNumber(advanceVerificationTotals.advance)}</td>
              <td>{formatNumber(advanceVerificationTotals.advanceGiven)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      </div>
    </>
  )
}

export default SalaryStatement
